import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronsLeftRight, MessageCircle } from "lucide-react";
import { useLanguage, waLink } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];
const BEFORE_FILTER = "saturate(0.35) brightness(0.72) contrast(0.92) sepia(0.28)";

const Compare = ({ src, alt, beforeLabel, afterLabel, testid }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      setPos(Math.min(94, Math.max(6, x)));
    };
    const up = () => {
      dragging.current = false;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, []);

  const jump = (e) => {
    dragging.current = true;
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(94, Math.max(6, x)));
  };

  return (
    <div
      ref={ref}
      data-testid={testid}
      onPointerDown={jump}
      className="relative aspect-[4/3] cursor-ew-resize touch-none select-none overflow-hidden rounded-3xl shadow-xl shadow-[#0D1B2A]/10"
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={src}
          alt=""
          aria-hidden="true"
          draggable={false}
          className="h-full w-full object-cover"
          style={{ filter: BEFORE_FILTER }}
        />
      </div>

      <span className="absolute left-4 top-4 rounded-full bg-[#0D1B2A]/80 px-3.5 py-1.5 text-xs font-bold uppercase tracking-widest text-white backdrop-blur-sm">
        {beforeLabel}
      </span>
      <span className="absolute right-4 top-4 rounded-full bg-[#25D366] px-3.5 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
        {afterLabel}
      </span>

      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-white shadow" />
        <button
          data-testid={`${testid}-handle`}
          aria-label={alt}
          className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#0D1B2A] shadow-xl transition-transform duration-200 hover:scale-110"
        >
          <ChevronsLeftRight size={20} />
        </button>
      </div>
    </div>
  );
};

const ITEMS = [
  {
    id: "compare-sofa",
    src: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?q=80&w=1000&auto=format&fit=crop",
    key: "sofa",
  },
  {
    id: "compare-carpet",
    src: "/images/limpieza-alfombra.jpg",
    key: "carpet",
  },
];

const BeforeAfter = () => {
  const { t } = useLanguage();
  const waMsg = t.services.items[6].wa;

  return (
    <section className="py-24 sm:py-32" data-testid="before-after-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 text-center">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C85A32]"
          >
            {t.beforeAfter.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
            className="font-serif text-4xl font-black tracking-tight text-[#0D1B2A] sm:text-5xl"
          >
            {t.beforeAfter.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
            className="mx-auto mt-4 max-w-xl text-base text-[#0D1B2A]/60"
          >
            {t.beforeAfter.sub}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {ITEMS.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: i * 0.12, ease: EASE }}
            >
              <Compare
                src={item.src}
                alt={t.beforeAfter[item.key]}
                beforeLabel={t.beforeAfter.before}
                afterLabel={t.beforeAfter.after}
                testid={item.id}
              />
              <p className="mt-4 text-center font-serif text-xl font-bold text-[#0D1B2A]">
                {t.beforeAfter[item.key]}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
          className="mt-12 flex justify-center"
        >
          <a
            data-testid="before-after-cta"
            href={waLink(waMsg)}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 text-base font-bold text-white shadow-xl shadow-[#25D366]/30 transition-all duration-300 hover:scale-[1.04]"
          >
            <MessageCircle size={20} strokeWidth={2.4} className="transition-transform duration-300 group-hover:-rotate-12" />
            {t.beforeAfter.cta}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default BeforeAfter;
